import { assert } from "@faro-lotv/foundation";
import { isFileExt, type AnyFile } from "@utils/file-ext";
import * as zip from "@zip.js/zip.js";


const ZIP_NOT_CREATED = "Zip file not yet created. Call ensureZipBlobExists() first.";

/**
 * ## ZipFile
 * This class acts like a file, but allows to add multiple files
 * and creates a zip file out of them.
 * Before using slice() or stream(), you need to call ensureZipBlobExists()
 *
 * Usage example:
 * ```typescript
 * file = new File(["HELLO1"], "file.txt");
 * files = [file, ...];
 * const zipFile = new ZipFile(files, "archive.zip", { lastModified: Date.now() });
 * ```
 */
export interface ZipFile extends File {
  files: AnyFile[];
  name: string;
  lastModified: number;
  isFaroZipFile: boolean;
  getZipFile(): File;
  ensureZipBlobExists(): Promise<Blob>;
}

interface ZipFileConstructor {
  new(files: AnyFile[], fileName: string, options?: FilePropertyBag): ZipFile;
  prototype: ZipFile;
}

// Implementation of the ZipFile class
export const ZipFile: ZipFileConstructor = class ZipFileImpl implements ZipFile {
  name: string;
  lastModified: number;
  type: string = "application/zip";
  files: AnyFile[];
  webkitRelativePath: string;
  isFaroZipFile = true;

  #zipBlobPromise: Promise<Blob> | null = null;
  #zipBlob: Blob | null = null;
  #size: number = 0;

  constructor(files: AnyFile[], fileName: string, options: FilePropertyBag = {}) {
    this.files = files;
    this.name = fileName;
    this.webkitRelativePath = fileName;
    this.lastModified = options.lastModified ?? Date.now();
    this.#size = files.reduce((acc, file) => acc + file.size, 0);
  }

  get size(): number {
    return this.#size;
  }

  /**
   * @returns The zip file as a native File.
   * @throws Error if the zip file has not been created yet.
   */
  getZipFile(): File {
    if (!this.#zipBlob) {
      throw new Error(ZIP_NOT_CREATED);
    }
    return new File([this.#zipBlob], this.name, {
      lastModified: this.lastModified,
      type: this.type,
    });
  }

  async ensureZipBlobExists(): Promise<Blob> {
    if (this.#zipBlob) {
      return this.#zipBlob;
    }

    // Use promise to prevent concurrent zip creation
    if (!this.#zipBlobPromise) {
      this.#zipBlobPromise = this.#createZipFileStream(this.files);
    }
    this.#zipBlob = await this.#zipBlobPromise;
    this.#size = this.#zipBlob.size;
    // Keeping the zipPromise alive, so it can be awaited again in case of a racing condition

    return this.#zipBlob;
  }

  async arrayBuffer(): Promise<ArrayBuffer> {
    const blob = await this.ensureZipBlobExists();
    return blob.arrayBuffer();
  }

  async text(): Promise<string> {
    const blob = await this.ensureZipBlobExists();
    return blob.text();
  }

  async bytes(): ReturnType<typeof Blob.prototype.bytes> {
    const blob = await this.ensureZipBlobExists();
    return new Uint8Array(await blob.arrayBuffer());
  }

  slice(start?: number, end?: number, contentType?: string): Blob {
    if (!this.#zipBlob) {
      throw new Error(ZIP_NOT_CREATED);
    }
    return this.#zipBlob.slice(start, end, contentType);
  }

  stream(): ReadableStream {
    if (!this.#zipBlob) {
      throw new Error(ZIP_NOT_CREATED);
    }
    return this.#zipBlob.stream();
  }

  async #createZipFileStream(files: AnyFile[]): Promise<Blob> {
    const blobWriter = new zip.BlobWriter("application/zip");
    const zipWriter = new zip.ZipWriter(blobWriter);

    // Sort files for deterministic order
    files.sort((a, b) => {
      const nameA = a.webkitRelativePath || a.name;
      const nameB = b.webkitRelativePath || b.name;
      return nameA.localeCompare(nameB, "en", { sensitivity: "base" });
    });

    // Use a fixed date for consistent zip file
    const fixedDate = new Date("2000-01-01T00:00:00Z");

    const addPromises = [];
    // Add all files to the zip
    for (const file of files) {
      const reader = new zip.BlobReader(file);
      const fileName = file.webkitRelativePath || file.name;
      addPromises.push(zipWriter.add(fileName, reader, {
        creationDate: fixedDate,
        lastModDate: fixedDate,
        lastAccessDate: fixedDate,
      }));
    }
    await Promise.all(addPromises);

    await zipWriter.close();
    return await blobWriter.getData();
  }

  // Can make it easier to check the correct type of the object when debugging.
  get [Symbol.toStringTag](): string {
    return "ZipFile";
  }
};

/** Type guard to check if it is a ZipFile, or a serialized ZipFile (e.g. from postMessage()). */
export function isZipFile(file: unknown): file is ZipFile {
  return file instanceof ZipFile ||
    !!((file as ZipFile).isFaroZipFile && Array.isArray((file as ZipFile).files));
}

/**
 * @param file Native File, or FileExt, or ZipFile.
 * @returns The underlying File, so it can be used with any API that expects a File.
 * @throws Error if the file is a ZipFile, and it hasn't been generated yet.
 *         Error if the file is a serialized ZipFile (e.g. from postMessage()).
 */
export function getRealFile(file: AnyFile | ZipFile): File {
  if (file instanceof ZipFile) {
    return file.getZipFile();
  }
  assert(!isZipFile(file), "getRealFile: Serialized ZipFile not supported.");

  if (isFileExt(file)) {
    return file.file;
  } else {
    return file;
  }
}
